import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiDelete, apiErrorMessage } from "../api/client";

/**
 * Who receives a report.
 *
 * These are addresses, not memberships: the executives who read the daily
 * pack are not the same people as the members who log in, and a Head of
 * Savings who never opens the product still needs it at 06:30. Until someone
 * is named here the report goes to the workspace's supervisors, so a
 * workspace that has configured nothing is not a workspace that receives
 * nothing.
 *
 * Rendered both against a decision on the telemetry page and as a list in
 * Settings, which is where a person actually goes looking for it.
 */

export interface Recipient {
  id: string;
  hand_key: string;
  decision_key: string;
  email: string;
  display_name: string | null;
  role_title: string | null;
}

export function ReportRecipients({
  workspaceId,
  handKey,
  decisionKey,
  decisionLabel,
  canEdit,
  heading = "WHO RECEIVES IT",
}: {
  workspaceId: string;
  handKey: string;
  decisionKey: string;
  decisionLabel: string;
  canEdit: boolean;
  heading?: string;
}) {
  const qc = useQueryClient();
  const list = useQuery<{ recipients: Recipient[] }, Error>({
    queryKey: ["report-recipients", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/report-recipients`),
  });
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");

  // The directory first: most recipients are already people the organisation
  // has named, and re-typing an address is how a list goes stale.
  const directory = useQuery<{ people: { id: string; name: string; email: string; title: string | null }[] }, Error>({
    queryKey: ["org-people", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/org/people`),
  });

  const add = useMutation({
    mutationFn: () =>
      apiPost(`/api/workspaces/${workspaceId}/report-recipients`, {
        hand_key: handKey,
        decision_key: decisionKey,
        email,
        role_title: title || undefined,
      }),
    onSuccess: () => {
      setEmail("");
      setTitle("");
      void qc.invalidateQueries({ queryKey: ["report-recipients", workspaceId] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/workspaces/${workspaceId}/report-recipients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["report-recipients", workspaceId] }),
  });

  const mine = (list.data?.recipients ?? []).filter(
    (r) => r.hand_key === handKey && r.decision_key === decisionKey,
  );

  return (
    <div style={{ marginTop: 12 }} data-testid={`recipients-${decisionKey}`}>
      <div className="section-eyebrow">{heading}</div>
      {mine.length === 0 ? (
        <div className="play-meta">
          <span>
            Nobody yet — this report goes to the workspace's supervisors until
            someone is named here.
          </span>
        </div>
      ) : (
        <div className="play-meta">
          {mine.map((r) => (
            <span key={r.id}>
              {r.role_title ? `${r.role_title} · ` : ""}
              {r.email}
              {canEdit ? (
                <>
                  {" "}
                  <button
                    type="button"
                    className="btn"
                    data-testid={`remove-recipient-${r.id}`}
                    onClick={() => remove.mutate(r.id)}
                  >
                    remove
                  </button>
                </>
              ) : null}
            </span>
          ))}
        </div>
      )}
      {canEdit ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          {(directory.data?.people ?? []).length > 0 ? (
            <select
              aria-label={`Add someone to ${decisionLabel}`}
              value=""
              onChange={(e) => {
                const person = (directory.data?.people ?? []).find((p) => p.id === e.target.value);
                if (person) {
                  setEmail(person.email);
                  setTitle(person.title ?? "");
                }
              }} style={{ minWidth: 200 }}
            >
              <option value="">From the organisation…</option>
              {(directory.data?.people ?? [])
                .filter((p) => !mine.some((r) => r.email.toLowerCase() === p.email.toLowerCase()))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.title ? ` — ${p.title}` : ""}
                  </option>
                ))}
            </select>
          ) : null}
          <input
            aria-label={`Email for ${decisionLabel}`}
            placeholder="name@nationalbonds.ae"
            value={email}
            onChange={(e) => setEmail(e.target.value)} style={{ minWidth: 220 }}
          />
          <input
            aria-label={`Title for ${decisionLabel}`}
            placeholder="Head of Savings (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)} style={{ minWidth: 180 }}
          />
          <button
            type="button"
            className="btn"
            data-testid={`add-recipient-${decisionKey}`}
            disabled={add.isPending || !email.includes("@")}
            onClick={() => add.mutate()}
          >
            Add to the list
          </button>
          {add.isError ? (
            <span className="pill red">
              {apiErrorMessage(add.error)}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
